import { Button, ConfigProvider, Dropdown, message, Modal, Table, Tag } from "antd"
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom";
import { CheckCircleOutlined, CloseOutlined, DownOutlined, EditOutlined, ExclamationCircleFilled, PlusOutlined, SaveOutlined, SendOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { AuthData, formatRupiah } from "../../Components/Global";
import FilterInvoice from "./FilterInvoice";
import { encrypt } from "../../Utils/CryptoHelper";
import { UpdateInvoice } from "../../API/UpdateData";
import { PostAllEmailINV } from "../../API/PostData";

const Invoice = () => {

    const Auth = AuthData();
    const navigate = useNavigate();

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingPublish, setLoadingPublish] = useState(false);
    const [loadingCancel, setLoadingCancel] = useState(false);

    const [isOpenPublish, setIsOpenPublish] = useState(false)
    const [isOpenCancel, setIsOpenCancel] = useState(false);

    const [currentRecord, setCurrentRecord] = useState(null);
    const [updateData, setUpdateData] = useState(false);

    const showModalPublish = (record) => {
        setCurrentRecord(record);
        setIsOpenPublish(true);
    };

    const showModalCancel = (record) => {
        setCurrentRecord(record);
        setIsOpenCancel(true);
    };

    const handlePublish = async (AccountId, InvoiceCode) => {
        try {
            setLoadingPublish(true);
            const payload = {
                AccountId: AccountId,
                InvoiceCode: InvoiceCode,
                Status: "Published",
            }
            const response = await UpdateInvoice(AccountId, InvoiceCode, payload);
            if (response.status === 200) {
                message.success(response.data.message);
                setIsOpenPublish(false);
                setUpdateData(true);
            };
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingPublish(false);
        }
    }

    const handleCancel = async (AccountId, InvoiceCode) => {
        try {
            setLoadingCancel(true);
            const payload = {
                AccountId: AccountId,
                InvoiceCode: InvoiceCode,
                Status: "Cancel",
            }
            const response = await UpdateInvoice(AccountId, InvoiceCode, payload);
            if (response.status === 200) {
                message.success(response.data.message);
                setIsOpenCancel(false);
                setUpdateData(true);
            };
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingCancel(false);
        }
    }

    const handleEdit = (AccountId, InvoiceCode) => {
        const encryptedAccountId = encodeURIComponent(encrypt(AccountId));
        const encryptedInvoiceCode = encodeURIComponent(encrypt(InvoiceCode));

        navigate(`form/${encryptedAccountId}/${encryptedInvoiceCode}`);
    }

    const handleClosePublish = () => {
        setIsOpenPublish(false);
    };

    const handleCloseCancel = () => {
        setIsOpenCancel(false);
    };

    const [loadingSendEmail, setLoadingSendEmail] = useState(false);

    const handleSendEmail = async () => {
        try {
            setLoadingSendEmail(true);
            const res = await PostAllEmailINV(data);
            if (res.status === 200) {
                message.success(res.data.message);
                setUpdateData(true);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingSendEmail(false);
        }
    }


    const columns = [
        {
            key: "key",
            title: "#",
            dataIndex: "key",
            sorter: (a, b) => a.key - b.key,
        },
        {
            key: "InvoiceCode",
            title: "Invoice No",
            dataIndex: "InvoiceCode",
            sorter: (a, b) => a.InvoiceCode.localeCompare(b.InvoiceCode),
            render: (value, record) => <Link to={`${encodeURIComponent(encrypt(record.AccountId))}/${encodeURIComponent(encrypt(value))}`} className="text-second hover:text-second-600 font-medium">{value}</Link>
        },
        {
            key: "InvoiceDate",
            title: "Invoice Date",
            dataIndex: "InvoiceDate",
            sorter: (a, b) => a.InvoiceDate.localeCompare(b.InvoiceDate),
        },
        {
            key: "InvoiceSentDate",
            title: "Invoice Sent Date",
            dataIndex: "InvoiceSentDate",
            sorter: (a, b) => a.InvoiceSentDate.localeCompare(b.InvoiceSentDate),
            render: (date) => date ? dayjs(date).format("YYYY-MM-DD") : '-'
        },
        {
            key: "OrderQuotation",
            title: "Order Quotation",
            dataIndex: "OrderQuotation",
            sorter: (a, b) => a.OrderQuotation.localeCompare(b.OrderQuotation),
            render: (value, record) => (
                <Link to={`/dashboard/menuorder/order/${encodeURIComponent(encrypt(record.AccountId))}/${encodeURIComponent(encrypt(record.OrderCode))}`} className="text-second hover:text-second-600 font-medium">
                    {value}
                </Link>
            ),
        },
        {
            key: "InvoiceDueDate",
            title: "Invoice Due Date",
            dataIndex: "InvoiceDueDate",
            sorter: (a, b) => a.InvoiceDueDate.localeCompare(b.InvoiceDueDate),
            render: (date) => dayjs(date).format('YYYY-MM-DD')
        },
        {
            key: "NET",
            title: "NET",
            dataIndex: "NET",
            sorter: (a, b) => a.NET.localeCompare(b.NET),
            render: (value) => <p>Rp {formatRupiah(value)}</p>
        },
        {
            key: "PaymentStatus",
            title: "Payment Status",
            dataIndex: "PaymentStatus",
            sorter: (a, b) => a.PaymentStatus.localeCompare(b.PaymentStatus),
        },
        {
            key: "PaymentDate",
            title: "Payment Date",
            dataIndex: "PaymentDate",
            sorter: (a, b) => a.PaymentDate.localeCompare(b.PaymentDate),
            render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : "-"
        },
        {
            key: "Status",
            title: "Status",
            dataIndex: "Status",
            sorter: (a, b) => a.Status.localeCompare(b.Status),
            render: (value) => {
                let color = "";
                let label = "";

                switch (value) {
                    case "Process":
                        color = "gold";
                        label = "Process";
                        break;
                    case "Published":
                        color = "green";
                        label = "Published";
                        break;
                    case "Cancel":
                        color = "red";
                        label = "Cancel";
                        break;
                    default:
                        color = "default";
                        label = value;
                }

                return <Tag color={color}>{label}</Tag>;
            }
        },
    ];

    if (Auth.Role === "Admin") {
        columns.push({
            key: "Action",
            title: "Action",
            dataIndex: "Action",
            fixed: "right",
            render: (_, record) => {

                const items = [
                    {
                        key: '1',
                        label: (
                            <button
                                className="flex gap-2 hover:text-primary"
                                onClick={() => showModalPublish(record)}
                            >
                                <SaveOutlined /> Publish
                            </button>
                        ),
                    },
                    {
                        key: '2',
                        label: (
                            <button
                                className="flex gap-2 hover:text-second"
                                onClick={() => handleEdit(record.AccountId, record.InvoiceCode)}
                                aria-label="Edit"
                            >
                                <EditOutlined /> Edit
                            </button>
                        ),
                    },
                    {
                        key: '3',
                        label: (
                            <button
                                className="flex gap-2 hover:text-red-500"
                                onClick={() => showModalCancel(record)}
                            >
                                <CloseOutlined /> Cancel
                            </button>
                        ),
                    },
                ];

                return (
                    <div className="text-second hover:text-second-600 font-medium">
                        <div className="flex gap-2 w-full justify-end">
                            {record.Status === "Published" && (
                                <div className="text-second text-center text-lg">
                                    <CheckCircleOutlined />
                                </div>
                            )}

                            <Dropdown menu={{ items }} trigger={['click']}>
                                <button>
                                    More <DownOutlined />
                                </button>
                            </Dropdown>
                        </div>

                        {/* Modal Confirmation Publish Data */}
                        <Modal
                            centered
                            open={isOpenPublish}
                            closable={false}
                            confirmLoading={loadingPublish}
                            footer={
                                <div className="flex gap-2 justify-end">
                                    <Button
                                        key="submit"
                                        type="primary"
                                        loading={loadingPublish}
                                        onClick={() => {
                                            // Use the currentRecord state instead of directly accessing record
                                            handlePublish(currentRecord.AccountId, currentRecord.InvoiceCode);
                                        }}
                                    >
                                        Submit
                                    </Button>
                                    <Button disabled={loadingPublish} onClick={handleClosePublish}>Cancel</Button>
                                </div>
                            }
                        >
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                    <ExclamationCircleFilled className="text-2xl text-primary" />
                                    <h1 className="font-medium text-xl">
                                        Are you sure
                                        <span className="text-green-500 font-blod"> Publish </span>
                                        this data?
                                    </h1>
                                </div>
                                <p>COA Code : <span className="font-medium">{currentRecord?.InvoiceCode}</span></p>

                            </div>
                        </Modal>

                        {/* Modal Confirmation Cancel Data */}
                        <Modal
                            centered
                            open={isOpenCancel}
                            closable={false}
                            confirmLoading={loadingCancel}
                            footer={
                                <div className="flex gap-2 justify-end">
                                    <Button
                                        key="submit"
                                        type="primary"
                                        loading={loadingCancel}
                                        onClick={() => {
                                            // Use the currentRecord state instead of directly accessing record
                                            handleCancel(currentRecord.AccountId, currentRecord.InvoiceCode);
                                        }}
                                    >
                                        Submit
                                    </Button>
                                    <Button disabled={loadingCancel} onClick={handleCloseCancel}>Cancel</Button>
                                </div>
                            }
                        >
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                    <ExclamationCircleFilled className="text-2xl text-primary" />
                                    <h1 className="font-medium text-xl">
                                        Are you sure
                                        <span className="text-red-500 font-blod"> Cancel </span>
                                        this data?
                                    </h1>
                                </div>
                                <p>COA Code : <span className="font-medium">{currentRecord?.InvoiceCode}</span></p>

                                {/* {msg && (
                                    <div className="bg-primary px-2 py-1 rounded flex justify-between">
                                        <p className="text-red-600 font-medium">{msg}</p>
                                        <Link className="text-white hover:text-black" to="/dashboard/account">Add Data</Link>
                                    </div>
                                )} */}
                                {/* {!currentRecord?.SalesName && (
                                    <p className="text-red-600 font-medium">Sales Name NULL!</p>
                                )} */}

                            </div>
                        </Modal>

                    </div>
                );
            },
        });
    }

    return (
        <div className="bg-white p-4 rounded-md">

            <FilterInvoice
                dataSource={(values) => setData(values)}
                isLoading={(values) => setIsLoading(values)}
                updateData={updateData}
                onUpdateData={(value) => setUpdateData(value)}
            />

            {Auth && (Auth.Role === "Admin" || Auth.Role === "SuperAdmin") && (
                <div className="flex justify-end my-4 gap-2">
                    <ConfigProvider
                        theme={{
                            components: {
                                Button: {
                                    colorPrimary: '#21b6d7',
                                    colorPrimaryHover: '#1aa0bd',
                                    colorPrimaryActive: '#178ca7',
                                    colorTextLightSolid: '#fff',
                                    lineWidth: 0,
                                },
                            },
                        }}
                    >
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={handleSendEmail}
                            loading={loadingSendEmail}
                        >
                            All Email
                        </Button>
                    </ConfigProvider>

                    <Link to={'form'}>
                        <Button type="primary" icon={<PlusOutlined />}>Add New</Button>
                    </Link>
                </div>
            )}

            <Table
                columns={columns}
                dataSource={data}
                loading={isLoading}
                scroll={{
                    x: 'max-content',
                }}
            />
        </div>
    )
}

export default Invoice